import { Link } from "react-router-dom";
import { Crown, ArrowUpRight, Sparkles, Lock, Check } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { tierLabel, tierAtLeast } from "@/lib/tier";

/**
 * InvestorTierCard — calm dashboard surface that shows the user's current
 * investor tier (or invites non-subs to upgrade) and highlights one or two
 * locked workflow features to educate on upgrade value.
 *
 * Renders nothing for users without an investor subscription AND no
 * relevant context (we don't want to nag homebuyers on every dashboard
 * visit — instead they see the standard "Subscriptions" card).
 *
 * Cost-Safe: zero API calls. Reads tier + features from /auth/me payload.
 */
export default function InvestorTierCard() {
    const { user } = useAuth();
    const tier = user?.tier?.tier; // investor_lite | investor_pro | investor_plus | null
    if (!tier) return null;

    const label = tierLabel(tier);
    const isPlus = tier === "investor_plus";
    const isPro = tier === "investor_pro";
    const isLite = tier === "investor_lite";

    // Pick the next upgrade target + 2 representative features to highlight.
    let upgradeTo = null;
    let teaserFeatures = [];

    if (isLite) {
        upgradeTo = "investor_pro";
        teaserFeatures = [
            "Deal comparison dashboard",
            "Premium Closing Strategy on every report",
            "Investor notes & pipeline statuses",
        ];
    } else if (isPro) {
        upgradeTo = "investor_plus";
        teaserFeatures = [
            "Portfolio / pipeline overview",
            "Actual-vs-estimated quote tracking",
            "Bulk upload queue · advanced exports",
        ];
    }

    return (
        <div
            className="bl-card p-5 md:p-6 relative overflow-hidden"
            data-testid="investor-tier-card"
        >
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2.5">
                    <div className="w-9 h-9 rounded-lg bg-bl-teal-tint flex items-center justify-center">
                        <Crown className="w-4 h-4 text-bl-primary" />
                    </div>
                    <div>
                        <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                            Your plan
                        </div>
                        <div
                            className="font-display text-bl text-lg leading-tight"
                            data-testid="investor-tier-label"
                        >
                            {label}
                        </div>
                    </div>
                </div>
                {isPlus && (
                    <span className="bl-badge bl-badge-low inline-flex items-center gap-1">
                        <Sparkles className="w-3 h-3" />
                        Plus
                    </span>
                )}
            </div>

            {upgradeTo && (
                <>
                    <div className="mt-4">
                        <div className="text-[11px] tracking-[0.18em] uppercase text-bl-primary">
                            Unlocked with {tierLabel(upgradeTo)}
                        </div>
                        <ul className="mt-2 space-y-1.5 text-sm text-bl">
                            {teaserFeatures.map((f) => (
                                <li
                                    key={f}
                                    className="flex gap-2 leading-snug"
                                >
                                    <Lock className="w-3.5 h-3.5 text-bl-muted mt-0.5 shrink-0" />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <Link
                        to="/pricing"
                        className="bl-btn-secondary mt-4 w-full"
                        data-testid="investor-tier-upgrade-cta"
                    >
                        Upgrade to {tierLabel(upgradeTo)}{" "}
                        <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </>
            )}

            {isPlus && (
                <div className="mt-4 space-y-1.5 text-sm text-bl">
                    {[
                        "Portfolio / pipeline overview",
                        "Bulk upload queue",
                        "Highest support priority",
                    ].map((f) => (
                        <div
                            key={f}
                            className="flex gap-2 leading-snug"
                        >
                            <Check className="w-3.5 h-3.5 text-bl-primary mt-0.5 shrink-0" />
                            <span>{f}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export { InvestorTierCard };

/**
 * Helper hook-style export — lets callers branch on tier at the dashboard
 * level without re-implementing the comparison.
 */
export function useTierAtLeast(minTier) {
    const { user } = useAuth();
    return tierAtLeast(user?.tier?.tier, minTier);
}
